/*
 * A page's tabs, the one row every page uses (docs/ux-principles.md): a
 * site's and a server's sections, a site's mail domains, Settings. Where you
 * are is the brand underline. A tab with something inside that needs you gets
 * a small dot in its status color, with the words on hover and for screen
 * readers, the same dot as a rail's parts. On a phone the row runs to the
 * screen's edges and scrolls sideways (the one thing that may), fades at an
 * edge with more past it, and brings the current tab into view.
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { cx } from "../lib/cx";

export type TabTone = "bad" | "warn" | "info";

export type TabItem = {
  id: string;
  label: ReactNode;
  href: string;
  /** Something inside needs you: its status tone. */
  tone?: TabTone | null;
  /** What the dot means, for its tooltip and screen readers ("Needs attention"). */
  note?: string;
};

const DOT: Record<TabTone, string> = { bad: "bg-bad-fill", warn: "bg-warn-fill", info: "bg-info-fill" };
const NOTE: Record<TabTone, string> = { bad: "Needs attention", warn: "Potential issues", info: "Something's scheduled" };

const TAB =
  "relative inline-flex h-11 items-center gap-1.5 border-b-2 text-[14px] font-medium whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500";
const ON = "border-brand-500 text-fg";
const OFF = "border-transparent text-body hover:border-line-strong hover:text-fg";

/** Which edges have more tabs past them. */
function useOverflow(ref: RefObject<HTMLElement | null>) {
  const [more, setMore] = useState({ left: false, right: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setMore((m) => (m.left === left && m.right === right ? m : { left, right }));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
    };
  }, [ref]);
  return more;
}

const FADE = "24px";
const maskOf = (m: { left: boolean; right: boolean }) =>
  m.left || m.right ? `linear-gradient(to right, ${m.left ? "transparent" : "#000"} 0, #000 ${FADE}, #000 calc(100% - ${FADE}), ${m.right ? "transparent" : "#000"} 100%)` : undefined;

/**
 * `current`: the id of the tab you're on. Without it, a tab is current when the address is in it (a NavLink's
 * match), for rows whose tabs are routes of their own (Settings).
 */
export function TabRow({ label, items, current, className }: { label: string; items: TabItem[]; current?: string; className?: string }) {
  const row = useRef<HTMLUListElement>(null);
  const more = useOverflow(row);
  const { pathname } = useLocation();
  // On a phone, the current tab is brought into the row's view (sideways only, so the page doesn't jump).
  useEffect(() => {
    const ul = row.current;
    const on = ul?.querySelector<HTMLElement>('[aria-current="page"]');
    if (ul && on && ul.scrollWidth > ul.clientWidth) ul.scrollLeft = Math.max(0, on.offsetLeft - (ul.clientWidth - on.offsetWidth) / 2);
  }, [current, pathname]);
  const mask = maskOf(more);
  return (
    // Below sm the row bleeds into the page's 16px gutter (ui/PageLayout), so it runs edge to edge without widening the page.
    <nav aria-label={label} className={cx("-mx-4 border-b border-line sm:mx-0", className ?? "mb-6")}>
      <ul ref={row} className="relative -mb-px flex gap-6 overflow-x-auto px-4 [scrollbar-width:none] sm:px-0" style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}>
        {items.map((t) => {
          const inner = (
            <>
              {t.label}
              {t.tone ? (
                <>
                  <span aria-hidden="true" className={cx("h-1.5 w-1.5 shrink-0 rounded-full", DOT[t.tone])} />
                  <span className="sr-only"> ({t.note ?? NOTE[t.tone]})</span>
                </>
              ) : null}
            </>
          );
          const title = t.tone ? (t.note ?? NOTE[t.tone]) : undefined;
          return (
            <li key={t.id} className="shrink-0">
              {current === undefined ? (
                <NavLink to={t.href} preventScrollReset title={title} className={({ isActive }) => cx(TAB, isActive ? ON : OFF)}>
                  {inner}
                </NavLink>
              ) : (
                <Link to={t.href} preventScrollReset title={title} aria-current={t.id === current ? "page" : undefined} className={cx(TAB, t.id === current ? ON : OFF)}>
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
